'use client'

import { motion, Variants } from 'motion/react'
import Link from 'next/link'
import { Fragment, ReactNode, useCallback, useId, useMemo } from 'react'
import { LoadingSpinner } from '@/components/loading-spinner'
import { SvgOutlinedCheckmark } from '@/icons/svg-outlined-checkmark'
import { IOnClickEvent } from '@/typings/onclick-event'
import { run } from '@/utils/run'
import { cn } from '@/utils/styles/cn'
import { COLORS } from '@/utils/styles/colors'
import { rgba } from '@/utils/styles/rgba'
import { useMobile } from '@/utils/window/use-mobile'

export interface IListItem<T> {
    onClick?: (item: T) => void
    href?: string
    id: string | number
    label: ReactNode
    emoji?: string | null
    value: T
    selected?: boolean
    isLoading?: boolean
    inlineEndNode?: ReactNode
}

interface IListProps<T> {
    list: IListItem<T>[]
    onClick?: () => void
    className?: string
    onMouseEnter?: (item: T) => void
    onMouseLeave?: () => void
}

export function List<T>({
    list,
    onClick: propsOnClick,
    className,
    onMouseEnter,
    onMouseLeave,
}: IListProps<T>) {
    const componentId = useId()

    const variants: Variants = useMemo(
        () => ({
            idle: { backgroundColor: rgba(0, 0, 0, 0), transition: { duration: 0 } },
            hover: {
                backgroundColor: rgba(0, 0, 0, 0.02),
                transition: { duration: 0.03, ease: 'easeOut' },
            },
        }),
        []
    )

    const isMobile = useMobile()

    const onClick = useCallback(
        (item: IListItem<T>) => (e: IOnClickEvent) => {
            // If click was made to open in new tab, don't call onClick
            if (e.ctrlKey || e.metaKey) return
            item.onClick?.(item.value)
            propsOnClick?.()
        },
        [propsOnClick]
    )

    return (
        <div className={cn('flex flex-col w-full pb-10', className)}>
            {list.map((item, index, arr) => {
                const isLast = index === arr.length - 1

                const onItemClick = onClick(item)

                const listItemNode = (
                    <motion.div
                        variants={variants}
                        initial="idle"
                        animate="idle"
                        whileHover={isMobile ? undefined : 'hover'}
                        onMouseOver={isMobile ? undefined : () => onMouseEnter?.(item.value)}
                        onMouseLeave={isMobile ? undefined : onMouseLeave}
                        onClick={item.href ? undefined : onClick(item)}
                        className={cn(
                            'webkit-tap-transparent min-h-[40px] flex items-center cursor-pointer py-3',
                            item.selected || item.isLoading || item.inlineEndNode
                                ? 'justify-between'
                                : 'justify-start'
                        )}
                    >
                        <motion.div
                            className="flex items-center"
                            variants={{
                                idle: { x: 0 },
                                hover: { x: 3 },
                            }}
                            transition={{
                                duration: 0.15,
                                ease: 'easeInOut',
                            }}
                        >
                            {item.emoji && <p className="text-[20px] mr-3">{item.emoji}</p>}
                            <div
                                className={cn(
                                    'font-semibold text-[22px]',
                                    item.selected ? 'text-[#37D778]' : ''
                                )}
                            >
                                {item.label}
                            </div>
                        </motion.div>

                        {item.inlineEndNode && !item.isLoading && !item.selected && (
                            <motion.div
                                className="flex items-center"
                                variants={{
                                    idle: { x: 0 },
                                    hover: { x: -3 },
                                }}
                                transition={{
                                    duration: 0.15,
                                    ease: 'easeInOut',
                                }}
                            >
                                {item.inlineEndNode}
                            </motion.div>
                        )}

                        {item.selected && (
                            <div className="flex items-center justify-center relative bg-neotaste min-h-6 min-w-6 rounded-full">
                                <SvgOutlinedCheckmark color="white" size={14} />
                            </div>
                        )}

                        {!item.selected && item.isLoading && (
                            <div className="pr-6">
                                <LoadingSpinner color={COLORS.black} size={24} />
                            </div>
                        )}
                    </motion.div>
                )

                return (
                    <Fragment key={'list' + item.id + componentId}>
                        {run(() => {
                            if (!item.href) return listItemNode

                            return (
                                <Link href={item.href} onClick={onItemClick}>
                                    {listItemNode}
                                </Link>
                            )
                        })}

                        {!isLast && <div className="bg-black/10 w-full min-h-[1px]" />}
                    </Fragment>
                )
            })}
        </div>
    )
}
